import {
	Banknote,
	BarChart3,
	Brain,
	Briefcase,
	Building2,
	Code,
	Database,
	FileText,
	Film,
	Gamepad2,
	GraduationCap,
	HandCoins,
	Headset,
	HeartPulse,
	List,
	ListChecks,
	type LucideIcon,
	Megaphone,
	MessageCircle,
	Palette,
	Plane,
	Scale,
	Search,
	Server,
	Shield,
	ShoppingCart,
	Tag,
	UserSearch,
	Users,
	UsersRound,
	Workflow,
} from "lucide-react";

/**
 * Canonical agent category vocabulary. Shared with verifieddr (same slugs +
 * labels) so the two leaderboards stay interchangeable. Each agent carries up
 * to {@link MAX_AGENT_CATEGORIES} of these slugs in its `categories` array.
 */
export const AGENT_CATEGORIES = [
	{ value: "ai", label: "AI" },
	{ value: "marketing", label: "Marketing" },
	{ value: "seo", label: "SEO" },
	{ value: "sales", label: "Sales" },
	{ value: "analytics", label: "Analytics" },
	{ value: "development", label: "Development" },
	{ value: "design", label: "Design" },
	{ value: "productivity", label: "Productivity" },
	{ value: "automation", label: "Automation" },
	{ value: "ecommerce", label: "E-commerce" },
	{ value: "finance", label: "Finance" },
	{ value: "education", label: "Education" },
	{ value: "security", label: "Security" },
	{ value: "customer-support", label: "Customer Support" },
	{ value: "communication", label: "Communication" },
	{ value: "content", label: "Content" },
	{ value: "media", label: "Media" },
	{ value: "infrastructure", label: "Infrastructure" },
	{ value: "data", label: "Data" },
	{ value: "recruiting", label: "Recruiting" },
	{ value: "hr", label: "HR" },
	{ value: "legal", label: "Legal" },
	{ value: "real-estate", label: "Real Estate" },
	{ value: "healthcare", label: "Healthcare" },
	{ value: "travel", label: "Travel" },
	{ value: "gaming", label: "Gaming" },
	{ value: "communities", label: "Communities" },
	{ value: "directories", label: "Directories" },
	{ value: "agencies", label: "Agencies" },
] as const;

export type AgentCategory = (typeof AGENT_CATEGORIES)[number]["value"];

export const MAX_AGENT_CATEGORIES = 3;

const AGENT_CATEGORY_VALUES = new Set<string>(
	AGENT_CATEGORIES.map((category) => category.value),
);

// Slug tuple for places that need a Zod enum / readonly string list.
export const AGENT_CATEGORY_SLUGS = AGENT_CATEGORIES.map(
	(category) => category.value,
) as unknown as [AgentCategory, ...AgentCategory[]];

const AGENT_CATEGORY_ALIASES: Record<string, AgentCategory> = {
	directory: "directories",
	community: "communities",
	"e-commerce": "ecommerce",
	"developer-tools": "development",
	dev: "development",
	fintech: "finance",
};

// API callers often send display labels ("Customer Support") instead of slugs
// ("customer-support") — accept both, case-insensitively.
const AGENT_CATEGORY_LABEL_LOOKUP = new Map<string, AgentCategory>(
	AGENT_CATEGORIES.map((category) => [
		category.label.toLowerCase(),
		category.value,
	]),
);

export function normalizeAgentCategory(
	category: string | null | undefined,
): AgentCategory | null {
	const normalized = category?.trim().toLowerCase();
	if (!normalized) return null;
	const canonical = AGENT_CATEGORY_ALIASES[normalized] ?? normalized;
	if (AGENT_CATEGORY_VALUES.has(canonical)) {
		return canonical as AgentCategory;
	}
	return AGENT_CATEGORY_LABEL_LOOKUP.get(normalized) ?? null;
}

export function isAgentCategory(value: string): value is AgentCategory {
	return normalizeAgentCategory(value) !== null;
}

/**
 * Strict category parsing for submit/update inputs: normalizes and dedupes,
 * reports values that don't match any category (instead of dropping silently),
 * and caps at {@link MAX_AGENT_CATEGORIES}.
 */
export function parseAgentCategories(
	categoriesInput: string[] | null | undefined,
): {
	categories: AgentCategory[];
	invalid: string[];
} {
	const categories = new Set<AgentCategory>();
	const invalid: string[] = [];
	for (const category of categoriesInput ?? []) {
		const normalized = normalizeAgentCategory(category);
		if (normalized) categories.add(normalized);
		else invalid.push(category);
	}
	return {
		categories: Array.from(categories).slice(0, MAX_AGENT_CATEGORIES),
		invalid,
	};
}

/**
 * Coerce stored category values (slugs, arrays, or legacy single strings) into
 * a clean slug array. Tolerates JSON-stringified arrays and bare strings so
 * read paths never crash on older records.
 */
export function coerceAgentCategories(value: unknown): AgentCategory[] {
	let raw: unknown = value;
	if (typeof raw === "string") {
		const trimmed = raw.trim();
		if (trimmed.startsWith("[")) {
			try {
				raw = JSON.parse(trimmed);
			} catch {
				raw = [trimmed];
			}
		} else {
			raw = trimmed ? [trimmed] : [];
		}
	}
	if (!Array.isArray(raw)) return [];
	return parseAgentCategories(raw.map(String)).categories;
}

/** Join category slugs into a human label string ("AI, Marketing"). */
export function formatAgentCategories(value: unknown): string {
	return coerceAgentCategories(value)
		.map((category) => getAgentCategoryLabel(category))
		.join(", ");
}

export function getAgentCategoryLabel(category: string): string {
	const canonical = normalizeAgentCategory(category);
	const info = AGENT_CATEGORIES.find((item) => item.value === canonical);
	return info?.label ?? category;
}

// Per-category icon, keyed by slug. Keep in sync with AGENT_CATEGORIES.
export const CATEGORY_ICONS: Record<string, LucideIcon> = {
	ai: Brain,
	marketing: Megaphone,
	seo: Search,
	sales: HandCoins,
	analytics: BarChart3,
	development: Code,
	design: Palette,
	productivity: ListChecks,
	automation: Workflow,
	ecommerce: ShoppingCart,
	finance: Banknote,
	education: GraduationCap,
	security: Shield,
	"customer-support": Headset,
	communication: MessageCircle,
	content: FileText,
	media: Film,
	infrastructure: Server,
	data: Database,
	recruiting: UserSearch,
	hr: Users,
	legal: Scale,
	"real-estate": Building2,
	healthcare: HeartPulse,
	travel: Plane,
	gaming: Gamepad2,
	communities: UsersRound,
	directories: List,
	agencies: Briefcase,
};

export function getAgentCategoryIcon(category: string): LucideIcon {
	const canonical = normalizeAgentCategory(category);
	return (canonical && CATEGORY_ICONS[canonical]) || Tag;
}

export const CATEGORY_DESCRIPTIONS: Record<string, string> = {
	ai: "AI models, copilots, and intelligent automation.",
	marketing: "Advertising, email, growth, and demand generation.",
	seo: "Search optimization, rankings, and organic traffic.",
	sales: "CRM, lead generation, and revenue tooling.",
	analytics: "Data analysis, dashboards, and business intelligence.",
	development: "Tooling for software engineers and technical teams.",
	design: "Graphic design, UI/UX, and creative software.",
	productivity: "Tools to work faster and stay organized.",
	automation: "Workflows, integrations, and no-code automation.",
	ecommerce: "Online stores, marketplaces, and selling tools.",
	finance: "Fintech, banking, payments, crypto, and investing.",
	education: "EdTech, online courses, and learning platforms.",
	security: "Cybersecurity, privacy, and data protection.",
	"customer-support": "Help desks, chatbots, and support automation.",
	communication: "Messaging, social, and collaboration tools.",
	content: "Writing, content creation, and publishing.",
	media: "Streaming, video, audio, and entertainment.",
	infrastructure: "Cloud, hosting, hardware, and developer infra.",
	data: "Data pipelines, warehouses, and enrichment.",
	recruiting: "Hiring, talent acquisition, and sourcing.",
	hr: "People ops, payroll, and team management.",
	legal: "Legal tech, contracts, and compliance.",
	"real-estate": "Property management, housing, and proptech.",
	healthcare: "Wellness, medical tech, and health tracking.",
	travel: "Travel booking, guides, and trip planning.",
	gaming: "Video games, esports, and gaming tools.",
	communities: "Forums, groups, and community platforms.",
	directories: "Listings, catalogs, and discovery platforms.",
	agencies: "Agencies and done-for-you service businesses.",
};
